/** @module @liquidefy/code-editor/ai/ai-models @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AIModelState,
  AIModelId,
  AIModelSpecs,
  AIModelConfig,
  AIModelRoutingConfig,
  AIProviderType,
  ModelPerformanceMetrics,
  RoutingStrategy,
  CircuitBreakerConfig,
  AIRequest,
  AIConstraints,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asAIModelId,
  now,
  DEFAULT_AI_MODEL_CONFIG,
} from '../../code-editor.js';

// ═══ §2 MODEL REGISTRY — create and query model state ═══

/**
 * Create a new model state entry.
 */
export function createModelState(
  id: string,
  provider: AIProviderType,
  name: string,
  version: string,
  specs: AIModelSpecs,
  config?: Partial<AIModelConfig>,
): AIModelState {
  return {
    id: asAIModelId(id),
    provider,
    name,
    version,
    specs,
    config: {
      ...DEFAULT_AI_MODEL_CONFIG,
      ...config,
    },
    status: 'available',
    performance: {
      latencyP50Ms: 0,
      latencyP95Ms: 0,
      latencyP99Ms: 0,
      tokensPerSecond: 0,
      qualityScore: 0,
      reliabilityScore: 1,
      costPerMToken: 0,
    },
  };
}

/**
 * Update the model status.
 */
export function updateModelStatus(
  state: AIModelState,
  status: AIModelState['status'],
): AIModelState {
  return { ...state, status };
}

/**
 * Check if the model is available for use.
 */
export function isModelAvailable(state: AIModelState): boolean {
  return state.status === 'available' || state.status === 'ready';
}

/**
 * Check if the model is ready to accept requests.
 */
export function isModelReady(state: AIModelState): boolean {
  return state.status === 'ready';
}

/**
 * Check if the model is currently busy.
 */
export function isModelBusy(state: AIModelState): boolean {
  return state.status === 'busy' || state.status === 'loading';
}

/**
 * Get model capabilities list.
 */
export function modelCapabilities(state: AIModelState): string[] {
  return [...state.specs.capabilities];
}

/**
 * Check if the model supports a specific language.
 */
export function supportsLanguage(state: AIModelState, language: string): boolean {
  return state.specs.languages.includes(language) || state.specs.languages.includes('*');
}

// ═══ §3 MODEL SELECTION — score, filter, sort models ═══

/**
 * Strategy weights for different selection strategies.
 */
export const STRATEGY_WEIGHTS: Record<RoutingStrategy, {
  readonly quality: number;
  readonly cost: number;
  readonly latency: number;
  readonly reliability: number;
}> = {
  performance: { quality: 0.2, cost: 0.1, latency: 0.5, reliability: 0.2 },
  cost: { quality: 0.1, cost: 0.6, latency: 0.1, reliability: 0.2 },
  quality: { quality: 0.6, cost: 0.05, latency: 0.1, reliability: 0.25 },
  availability: { quality: 0.1, cost: 0.1, latency: 0.2, reliability: 0.6 },
  'round-robin': { quality: 0.25, cost: 0.25, latency: 0.25, reliability: 0.25 },
  weighted: { quality: 0.3, cost: 0.2, latency: 0.2, reliability: 0.3 },
  custom: { quality: 0.25, cost: 0.25, latency: 0.25, reliability: 0.25 },
};

/**
 * Select the best model from a list given requirements and strategy.
 */
export function selectModel(
  models: readonly AIModelState[],
  requirements: AIConstraints,
  strategy: RoutingStrategy,
): AIModelState | null {
  const available = filterByConstraints(models, requirements);
  if (available.length === 0) return null;
  const sorted = sortByStrategy(available, strategy);
  return sorted[0] ?? null;
}

/**
 * Score a model's fit for given requirements (0-1).
 */
export function scoreModelFit(model: AIModelState, requirements: AIConstraints): number {
  let score = 1;

  // Check token capacity
  if (requirements.maxTokens) {
    if (model.specs.maxOutputTokens < requirements.maxTokens) score *= 0.3;
  }

  // Check latency
  if (requirements.maxLatencyMs) {
    if (model.performance.latencyP50Ms > requirements.maxLatencyMs) score *= 0.4;
    else if (model.performance.latencyP95Ms > requirements.maxLatencyMs) score *= 0.7;
  }

  // Check capabilities
  if (requirements.requiredCapabilities) {
    const caps = new Set(model.specs.capabilities);
    const matched = requirements.requiredCapabilities.filter((c) => caps.has(c)).length;
    const total = requirements.requiredCapabilities.length;
    score *= total > 0 ? matched / total : 1;
  }

  // Factor in reliability
  score *= model.performance.reliabilityScore;

  // Factor in quality
  score *= (0.5 + model.performance.qualityScore * 0.5);

  return Math.max(0, Math.min(1, score));
}

/**
 * Filter models by required capabilities.
 */
export function filterByCapabilities(
  models: readonly AIModelState[],
  required: readonly string[],
): AIModelState[] {
  return models.filter((model) => {
    const caps = new Set(model.specs.capabilities);
    return required.every((r) => caps.has(r));
  });
}

/**
 * Filter models by constraints (tokens, latency, cost).
 */
export function filterByConstraints(
  models: readonly AIModelState[],
  constraints: AIConstraints,
): AIModelState[] {
  return models.filter((model) => {
    if (!isModelAvailable(model)) return false;
    if (constraints.maxTokens && model.specs.maxOutputTokens < constraints.maxTokens) return false;
    if (constraints.maxCost && model.performance.costPerMToken > constraints.maxCost * 1000) return false;
    if (constraints.requiredCapabilities) {
      const caps = new Set(model.specs.capabilities);
      if (!constraints.requiredCapabilities.every((c) => caps.has(c))) return false;
    }
    return true;
  });
}

/**
 * Sort models by the given routing strategy.
 */
export function sortByStrategy(
  models: readonly AIModelState[],
  strategy: RoutingStrategy,
): AIModelState[] {
  const weights = STRATEGY_WEIGHTS[strategy];
  return [...models].sort((a, b) => {
    const scoreA =
      a.performance.qualityScore * weights.quality +
      (1 - normalize(a.performance.costPerMToken, 0, 100)) * weights.cost +
      (1 - normalize(a.performance.latencyP50Ms, 0, 10000)) * weights.latency +
      a.performance.reliabilityScore * weights.reliability;
    const scoreB =
      b.performance.qualityScore * weights.quality +
      (1 - normalize(b.performance.costPerMToken, 0, 100)) * weights.cost +
      (1 - normalize(b.performance.latencyP50Ms, 0, 10000)) * weights.latency +
      b.performance.reliabilityScore * weights.reliability;
    return scoreB - scoreA; // Higher is better
  });
}

function normalize(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

// ═══ §4 ROUTING — fallback chains, request routing ═══

/**
 * Create a routing configuration.
 */
export function createRoutingConfig(
  primary: AIModelId,
  fallbacks: readonly AIModelId[],
  strategy?: RoutingStrategy,
): AIModelRoutingConfig {
  return {
    strategy: strategy ?? 'quality',
    fallback: [primary, ...fallbacks],
    circuitBreaker: DEFAULT_CIRCUIT_CONFIG,
  };
}

/**
 * Route a request to the best available model.
 */
export function routeRequest(
  _request: AIRequest,
  models: readonly AIModelState[],
  config: AIModelRoutingConfig,
): AIModelState | null {
  // Try models in fallback order
  for (const modelId of config.fallback) {
    const model = models.find((m) => m.id === modelId);
    if (model && isModelAvailable(model)) return model;
  }
  // If all fallbacks exhausted, try any available model
  const available = models.filter(isModelAvailable);
  if (available.length > 0) {
    return sortByStrategy(available, config.strategy)[0] ?? null;
  }
  return null;
}

/**
 * Get the next fallback model after failed models.
 */
export function nextFallback(
  config: AIModelRoutingConfig,
  failedModels: ReadonlySet<AIModelId>,
): AIModelId | null {
  for (const modelId of config.fallback) {
    if (!failedModels.has(modelId)) return modelId;
  }
  return null;
}

/**
 * Determine if we should fallback based on the error.
 */
export function shouldFallback(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    // Don't fallback on auth errors or bad requests
    if (msg.includes('auth') || msg.includes('unauthorized') || msg.includes('forbidden')) return false;
    if (msg.includes('invalid') || msg.includes('bad request')) return false;
    // Fallback on timeouts, rate limits, server errors
    return true;
  }
  return true;
}

/**
 * Get the full fallback chain from the config.
 */
export function fallbackChain(config: AIModelRoutingConfig): AIModelId[] {
  return [...config.fallback];
}

/**
 * Check if a model is the last in the fallback chain.
 */
export function isLastFallback(config: AIModelRoutingConfig, modelId: AIModelId): boolean {
  return config.fallback.length > 0 && config.fallback[config.fallback.length - 1] === modelId;
}

// ═══ §5 CIRCUIT BREAKER — failure tracking, state machine ═══

/**
 * Circuit breaker states.
 */
export const CIRCUIT_STATES = {
  CLOSED: 'closed',
  OPEN: 'open',
  HALF_OPEN: 'half-open',
} as const;

/**
 * Default circuit breaker configuration.
 */
export const DEFAULT_CIRCUIT_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5,
  resetTimeoutMs: 30000,
  halfOpenRequests: 3,
};

interface CircuitBreakerState {
  readonly state: 'closed' | 'open' | 'half-open';
  readonly failureCount: number;
  readonly successCount: number;
  readonly lastFailureTime: number;
  readonly config: CircuitBreakerConfig;
}

/**
 * Create an initial circuit breaker state.
 */
export function createCircuitBreaker(config?: Partial<CircuitBreakerConfig>): CircuitBreakerState {
  return {
    state: 'closed',
    failureCount: 0,
    successCount: 0,
    lastFailureTime: 0,
    config: { ...DEFAULT_CIRCUIT_CONFIG, ...config },
  };
}

/**
 * Record a successful request.
 */
export function recordSuccess(state: CircuitBreakerState): CircuitBreakerState {
  if (state.state === 'half-open') {
    const newSuccessCount = state.successCount + 1;
    if (newSuccessCount >= state.config.halfOpenRequests) {
      // Transition to closed
      return { ...state, state: 'closed', failureCount: 0, successCount: 0 };
    }
    return { ...state, successCount: newSuccessCount };
  }
  return { ...state, failureCount: 0, successCount: state.successCount + 1 };
}

/**
 * Record a failed request.
 */
export function recordFailure(state: CircuitBreakerState): CircuitBreakerState {
  const newFailureCount = state.failureCount + 1;
  if (newFailureCount >= state.config.failureThreshold) {
    return { ...state, state: 'open', failureCount: newFailureCount, lastFailureTime: now() };
  }
  if (state.state === 'half-open') {
    return { ...state, state: 'open', failureCount: newFailureCount, lastFailureTime: now() };
  }
  return { ...state, failureCount: newFailureCount, lastFailureTime: now() };
}

/**
 * Check if the circuit is open (blocking requests).
 */
export function isCircuitOpen(state: CircuitBreakerState): boolean {
  return state.state === 'open';
}

/**
 * Check if the circuit is closed (allowing requests).
 */
export function isCircuitClosed(state: CircuitBreakerState): boolean {
  return state.state === 'closed';
}

/**
 * Check if the circuit is half-open (testing).
 */
export function isCircuitHalfOpen(state: CircuitBreakerState): boolean {
  return state.state === 'half-open';
}

/**
 * Determine if a request should be allowed through.
 */
export function shouldAllowRequest(state: CircuitBreakerState): boolean {
  if (state.state === 'closed') return true;
  if (state.state === 'half-open') return state.successCount < state.config.halfOpenRequests;
  // Open state: check if reset timeout has elapsed
  const elapsed = now() - state.lastFailureTime;
  return elapsed >= state.config.resetTimeoutMs;
}

/**
 * Reset the circuit breaker to closed state.
 */
export function resetCircuitBreaker(state: CircuitBreakerState): CircuitBreakerState {
  return { ...state, state: 'closed', failureCount: 0, successCount: 0 };
}

// ═══ §6 COST ESTIMATION — pricing, budgets, usage tracking ═══

/**
 * Provider pricing (approximate per million tokens).
 */
export const PROVIDER_PRICING: Record<AIProviderType, Record<string, number>> = {
  anthropic: { 'claude-3-opus': 75, 'claude-3-sonnet': 15, 'claude-3-haiku': 1.25, 'default': 15 },
  openai: { 'gpt-4o': 10, 'gpt-4-turbo': 30, 'gpt-3.5-turbo': 1, 'default': 10 },
  google: { 'gemini-pro': 7, 'gemini-ultra': 30, 'default': 7 },
  microsoft: { 'default': 10 },
  meta: { 'llama-3': 0, 'default': 0 },
  cohere: { 'command-r': 3, 'default': 3 },
  huggingface: { 'default': 0.5 },
  local: { 'default': 0 },
  custom: { 'default': 5 },
};

/**
 * Estimate cost for a given model and token counts.
 */
export function estimateCost(model: AIModelState, inputTokens: number, outputTokens: number): number {
  const providerPricing = PROVIDER_PRICING[model.provider] ?? PROVIDER_PRICING['custom'];
  const rate = providerPricing[model.name] ?? providerPricing['default'] ?? 5;
  const totalTokens = inputTokens + outputTokens;
  return (totalTokens / 1_000_000) * rate;
}

/**
 * Track token usage on a model state.
 */
export function trackUsage(
  state: AIModelState,
  tokens: { readonly input: number; readonly output: number },
): AIModelState {
  // Performance metrics are updated via updatePerformanceMetrics
  return state;
}

/**
 * Compute total cost from a history of (tokens, model) pairs.
 */
export function totalCost(history: readonly { readonly tokens: number; readonly costPerMToken: number }[]): number {
  return history.reduce((sum, h) => sum + (h.tokens / 1_000_000) * h.costPerMToken, 0);
}

/**
 * Average cost per request.
 */
export function costPerRequest(history: readonly { readonly tokens: number; readonly costPerMToken: number }[]): number {
  if (history.length === 0) return 0;
  return totalCost(history) / history.length;
}

/**
 * Cost for a specific day.
 */
export function dailyCost(
  history: readonly { readonly tokens: number; readonly costPerMToken: number; readonly timestamp: number }[],
  date: Date,
): number {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayEnd = dayStart + 86400000;
  const dayEntries = history.filter((h) => h.timestamp >= dayStart && h.timestamp < dayEnd);
  return totalCost(dayEntries);
}

/**
 * Cost for a specific month.
 */
export function monthlyCost(
  history: readonly { readonly tokens: number; readonly costPerMToken: number; readonly timestamp: number }[],
  month: Date,
): number {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1).getTime();
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 1).getTime();
  const monthEntries = history.filter((h) => h.timestamp >= monthStart && h.timestamp < monthEnd);
  return totalCost(monthEntries);
}

/**
 * Remaining budget.
 */
export function budgetRemaining(budget: number, spent: number): number {
  return Math.max(0, budget - spent);
}

/**
 * Check if budget is exceeded.
 */
export function isBudgetExceeded(budget: number, spent: number): boolean {
  return spent > budget;
}

// ═══ §7 PERFORMANCE TRACKING — latency, throughput, metrics ═══

/**
 * Record a latency observation.
 */
export function recordLatency(state: AIModelState, latencyMs: number): AIModelState {
  const perf = state.performance;
  // Simple running average approximation
  const newP50 = perf.latencyP50Ms === 0 ? latencyMs : perf.latencyP50Ms * 0.9 + latencyMs * 0.1;
  const newP95 = latencyMs > perf.latencyP95Ms ? latencyMs : perf.latencyP95Ms * 0.95 + latencyMs * 0.05;
  const newP99 = latencyMs > perf.latencyP99Ms ? latencyMs : perf.latencyP99Ms * 0.99 + latencyMs * 0.01;
  return {
    ...state,
    performance: {
      ...perf,
      latencyP50Ms: Math.round(newP50),
      latencyP95Ms: Math.round(newP95),
      latencyP99Ms: Math.round(newP99),
    },
  };
}

/**
 * Record a throughput observation.
 */
export function recordThroughput(state: AIModelState, tps: number): AIModelState {
  const perf = state.performance;
  const newTps = perf.tokensPerSecond === 0 ? tps : perf.tokensPerSecond * 0.8 + tps * 0.2;
  return {
    ...state,
    performance: { ...perf, tokensPerSecond: Math.round(newTps * 100) / 100 },
  };
}

/**
 * Update all performance metrics at once.
 */
export function updatePerformanceMetrics(
  state: AIModelState,
  metrics: Partial<ModelPerformanceMetrics>,
): AIModelState {
  return {
    ...state,
    performance: { ...state.performance, ...metrics },
  };
}

/**
 * Compute a latency percentile from a history of latency values.
 */
export function latencyPercentile(history: readonly number[], percentile: number): number {
  if (history.length === 0) return 0;
  const sorted = [...history].sort((a, b) => a - b);
  const idx = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)]!;
}

/**
 * Check if performance has degraded compared to a baseline.
 */
export function isPerformanceDegraded(state: AIModelState, baseline: ModelPerformanceMetrics): boolean {
  // Degraded if latency increased by >50% or quality dropped by >20%
  const latencyRatio = state.performance.latencyP50Ms / (baseline.latencyP50Ms || 1);
  const qualityDrop = baseline.qualityScore - state.performance.qualityScore;
  return latencyRatio > 1.5 || qualityDrop > 0.2;
}

/**
 * Generate a human-readable performance report.
 */
export function performanceReport(state: AIModelState): string {
  const p = state.performance;
  const lines: string[] = [];
  lines.push(`Model: ${state.provider}/${state.name} v${state.version}`);
  lines.push(`Status: ${state.status}`);
  lines.push(`Latency: p50=${p.latencyP50Ms}ms, p95=${p.latencyP95Ms}ms, p99=${p.latencyP99Ms}ms`);
  lines.push(`Throughput: ${p.tokensPerSecond} tokens/sec`);
  lines.push(`Quality: ${(p.qualityScore * 100).toFixed(1)}%`);
  lines.push(`Reliability: ${(p.reliabilityScore * 100).toFixed(1)}%`);
  lines.push(`Cost: $${p.costPerMToken}/M tokens`);
  return lines.join('\n');
}
